import React, { useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2 } from "lucide-react";

export const ANSWERS = [
  { v: "yes", label: "Yes", on: "bg-rag-green text-white", hover: "hover:bg-rag-green-bg" },
  { v: "partial", label: "Partially", on: "bg-rag-amber text-white", hover: "hover:bg-rag-amber-bg" },
  { v: "no", label: "No", on: "bg-rag-red text-white", hover: "hover:bg-rag-red-bg" },
  { v: "na", label: "N/A", on: "bg-brand text-white", hover: "hover:bg-brand-light" },
];

const SCORE = { yes: 1, partial: 0.5 };
export const scoreOf = (v) => SCORE[v] ?? 0;
export const isAnswered = (v) => ["yes", "partial", "no", "na"].includes(v);
export const needsAction = (v) => v === "no" || v === "partial";
export const NO_ACTION_TEXT = "No action required — standard met";

const ACTION_BOX = {
  yes: { label: "Action / improvement (optional)", placeholder: "Standard met — tick 'No action required', or note an optional improvement, owner and date." },
  partial: { label: "Action required / responsible person / deadline", placeholder: "e.g. 'Update refusal strategy — Senior Carer J.S. — by 30/06.'", required: true },
  no: { label: "Action required / responsible person / deadline", placeholder: "e.g. 'Update refusal strategy — Senior Carer J.S. — by 30/06.'", required: true },
  na: { label: "Reason not applicable", placeholder: "e.g. 'Resident has no natural teeth or dentures — denture care does not apply.'", required: true },
};
const DEFAULT_BOX = { label: "Action required / responsible person / deadline", placeholder: "Select an answer above, then record any action, owner and deadline." };

function NoActionButton({ action, onAction, testId, inputRef }) {
  const active = (action || "").trim() === NO_ACTION_TEXT;
  const click = () => {
    const cur = (action || "").trim();
    if (active) { onAction(""); if (inputRef.current) inputRef.current.value = ""; return; }
    if (cur && !window.confirm("Replace the recorded action with “No action required”?")) return;
    onAction(NO_ACTION_TEXT); if (inputRef.current) inputRef.current.value = NO_ACTION_TEXT;
  };
  return (
    <button type="button" data-testid={`${testId}-no-action`} onClick={click}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${active ? "bg-rag-green text-white" : "bg-rag-green-bg text-rag-green hover:bg-rag-green hover:text-white"}`}>
      <CheckCircle2 className="h-3 w-3" />No action required
    </button>
  );
}

export function AuditItemRow({ item, hint, value, evidence, action, onAnswer, onEvidence, onAction, testId, disabled, badge }) {
  const actionRef = useRef(null);
  const box = ACTION_BOX[value] || DEFAULT_BOX;
  const answered = isAnswered(value);
  const missingAction = box.required && !(action || "").trim();
  const missingEvidence = answered && value !== "na" && !(evidence || "").trim();
  return (
    <div className="py-3 border-b border-cream last:border-0" data-testid={`${testId}-row`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex-1 min-w-[200px] text-sm flex items-center gap-2">{item}{badge}</span>
        <div className={`flex gap-1 flex-wrap ${disabled ? "opacity-70" : ""}`}>
          {ANSWERS.map((a) => (
            <button key={a.v} type="button" data-testid={`${testId}-${a.v}`} disabled={disabled} onClick={() => onAnswer(a.v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${value === a.v ? a.on : `bg-cream text-muted-foreground ${a.hover}`} ${disabled ? "cursor-not-allowed" : ""}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>
      {!disabled && answered && (
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <div>
            <label className="text-[11px] font-semibold text-brand/80 mb-1 block">Evidence reviewed and findings{value !== "na" && <span className="text-coral"> *</span>}</label>
            <Textarea data-testid={`${testId}-evidence`} rows={2} placeholder={hint || "What you checked, the date, and what it confirmed"} defaultValue={evidence || ""}
              onBlur={(e) => onEvidence(e.target.value)} className={`min-h-0 text-xs bg-white resize-y ${missingEvidence ? "border-rag-amber/60" : ""}`} />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="text-[11px] font-semibold text-brand/80">{box.label}{box.required && <span className="text-coral"> *</span>}</label>
              {value === "yes" && <NoActionButton action={action} onAction={onAction} testId={testId} inputRef={actionRef} />}
            </div>
            <Textarea ref={actionRef} data-testid={`${testId}-action`} rows={2} placeholder={box.placeholder} defaultValue={action || ""}
              onBlur={(e) => onAction(e.target.value)} className={`min-h-0 text-xs bg-white resize-y ${missingAction ? "border-rag-red/60" : ""}`} />
          </div>
        </div>
      )}
    </div>
  );
}
